<template>
  <div class="project-page"
       :class="{'modal-fix': modalVisible}"
       ref="projectView">
    <div class="all-content">
      <loader :loading="projectLoading" />
      <project-header v-if="loaded"
                      ref="projectHeader"
                      :fromTemplate="fromTemplate"
                      :fromPostCreate="fromPostCreate"
                      :fromStartButtonTemplate="fromStartButtonTemplate"
                      :checkCanSave="checkCanSave"
                      :projectLoading="projectLoading"
                      @projectSaved="projectSaved"
                      @saveProject="saveProject"
                      @isDeleting="isDeleting"
                      :classId="classId"
                      :projectHeight="projectHeight"
                      :tagsysc="this.tagsysc" />
      <tab-selector v-if="loaded && showTabs"
                    :startTab="startTab" />
      <div class="buttons-container is-flex">
        <live-chat-button   v-if="loaded && showChatButton"
                            attributes="live-chat"
                            :liveChat="$t('projectPage.liveChat')"
                            :toggle="callLive"
                            :status="projectIsOnline"
                            :role="userRole"
                            />
        <project-view-button v-if="loaded && !notFound && canCreateSection"
                            :disabled="showCreate || maxSectionsReached"
                            :toggle="toggleShowCreate"
                            attributes="new-section"
                            :newSection="$t('projectPage.new')"
                            :section="$t('projectPage.section')"/>
      </div>
      <live-chat-modal :project="project" v-if="showLiveChat" @close="callLive"></live-chat-modal>
      <create-inline v-if="showCreate"
                     @saveAndClose="saveAndClose"
                     @cancel="toggleShowCreate"
                     @setLastToFirst="setLastToFirst"
                     :project="project"
                     :context="'inline'"
                     @incrementPostCount="incrementPostCount"
                     @saved="toggleShowCreate"
                     @scrollToSectionTop="scrollToSectionTop" />
      <keep-alive>
        <component class="component"
                   :class="{'create-is-open' : showCreate}"
                   :stateKeyValue="keyName"
                   :is="activeTab"
                   :ref="activeTab"
                   :showCreate="showCreate"
                   :canCreatePost="canCreatePost" />
      </keep-alive>
      <board v-if="loaded" />
    </div>
    <scroll-to-top class="scroll-button" />
    <confirm ref="unsavedModal"
             :title="$t('projectPage.confirmCancel')"
             :text="cancelMessage"
             @yes="unsavedModalYes"
             @no="unsavedModalNo" />
    <modal-report />
  </div>
</template>

<script>
import Board from '@/components/MagicBoard/Modal'
import Confirm from '@/components/Modals/Confirm'
import ScrollToTop from '@/components/Controls/ScrollTopButton'
import CreateInline from '@/components/Forms/Create/Post'
import ModalReport from '@/components/UIReport/Modal'
import Loader from '@/components/Base/Loader/loader'
import ProjectHeader from './Header/ProjectHeader'

import TabSelector from './Tabs/Selector'
import List from './Tabs/List'
import Results from './Tabs/Results'
import Grid from './Tabs/Grid'
import Search from './Tabs/Search'
import Linked from './Tabs/Linked'
import NotFound from './Tabs/NotFound'
import Chat from './Tabs/Chat'
import LiveChat from './Tabs/LiveChat'
import Judging from './Tabs/Judging'
import tabs from './tabs'
import LiveChatModal from '@/components/Modals/Custom/LiveChatModal'
import LiveChatButton from './ProjectView/LiveChatButton'

import mixin from './index'
import state from './state'
import debounce from 'lodash/debounce'

import breakpoints from '@/libs/breakpoints'
import Vue from 'vue'
import i18n from '@/i18n'
import ProjectViewButton from './ProjectView/ProjectViewButton'
import displayTypes from '@/mixins/project-display-types'

export default {
  name: 'viewProject',

  components: {
    ProjectViewButton,
    Loader,
    ProjectHeader,
    TabSelector,
    Results,
    List,
    Grid,
    Search,
    Linked,
    NotFound,
    ScrollToTop,
    Board,
    Confirm,
    CreateInline,
    ModalReport,
    Chat,
    LiveChat,
    LiveChatModal,
    LiveChatButton,
    Judging
  },
  mixins: [mixin, tabs, displayTypes],

  data: function () {
    return {
      showTabs: true,
      openFlag: false,
      isDeletingInProgress: false,
      projectHeight: 0,
      showCreate: false,
      tagsysc: '',
      showLiveChat: false
    }
  },
  created: function () {
    this.attachScrollListener()
  },
  destroyed: function () {
    this.detachScrollListener()
  },
  beforeCreate: function () {
    const isProjectsRoute = this.$route.name === 'viewProject'
    if (this.$isEngCom && isProjectsRoute) {
      this.$store.dispatch('setShowingProjectOnEng', true)
      Vue.prototype.$wasProjects = this.$isProjects
      Vue.prototype.$wasStories = this.$isStories
      Vue.prototype.$wasEngCom = this.$isEngCom
      Vue.prototype.$isProjects = isProjectsRoute
    }
  },
  beforeRouteLeave: function (to, from, next) {
    if (this.$store.getters.isShowingProjectOnEng) {
      Vue.prototype.$isProjects = Vue.prototype.$wasProjects
      Vue.prototype.$isStories = Vue.prototype.$wasStories
      Vue.prototype.$isEngCom = Vue.prototype.$wasEngCom
      this.$store.dispatch('setShowingProjectOnEng', false)
    }
    next()
  },

  mounted: function () {
    this.startTab = 'grid'
    if (this.fromTemplate && (this.$isYSC || this.$isAjas)) {
      const data = {
        id: this.$route.query.projectid,
        categories: this.$route.query.category ? [this.$route.query.category[this.$route.query.category.length - 1]] : []
      }
      this.$store.dispatch('updateProjectCategories', data).then((response) => {
        if (response.categories?.length > 0) {
          this.$refs.projectHeader.categoryKey = response.categories[0].id
        }
      })
    }
    this.tagsysc = this.$route.query.tags
    setTimeout(() => {
      this.openFlag = true
    }, 8000)
  },

  computed: {
    userRole: function () {
      return this._get(state.project.role)
    },
    isProjectSubmitted: function () {
      return this.project.submitted
    },
    isProjectEditable: function () {
      return this.project.isEditable
    },
    canCreateSection: function () {
      return (this.isProjectEditable && this.canCreatePost && this.project.createSection) || this.isAdmin || this.projectRole === 'anonAdmin' || this.projectRole === 'admin'
    },
    classId: function () {
      return this.$route.query.classId
    },
    confirmSubmissionMessage: function () {
      return i18n.t('projectPage.confirmSubmission')
    },
    cancelMessage: function () {
      if (this.fromTemplate) {
        return i18n.t('projectPage.discardProject')
      }
      return i18n.t('projectPage.discardChanges')
    },
    isMobile: function () {
      return breakpoints.isMobile()
    },
    annotateKey: function () {
      return this.project ? `postFrom.${this.project.id}` : null
    },
    annotateFrom: function () {
      return this.annotateKey ? this._get(this.annotateKey, null) : null
    },
    tabKey: function () {
      return 'key_' + this.keyName ?? ''
    },
    maxSectionsReached: function () {
      if (this.project.maxSections === 0) {
        return false
      }
      return this.project.maxSections <= this.numPosts
    },
    isRegistrationForm: function () {
      return this.projectDisplayType === this.DisplayTypeRegistrationForm
    },
    showChatButton: function () {
      return this.$isAjas || this.$isYSC || (this.$isIsef && this.checkSourceProjectId())
    }
  },
  watch: {
    annotateFrom: function (attachment) {
      if (attachment) {
        this.toggleShowCreate()
      }
    }
  },
  methods: {
    checkSourceProjectId: function () {
      // We only want the ISEF finalist projects to have live chat
      const isef2022FinalistSourceId = 75550
      return (this.project.sourceProjectID === isef2022FinalistSourceId)
    },
    callLive: function () {
      this.showLiveChat = !this.showLiveChat
    },
    checkCanSave: function () {
      if (this.$refs.list) {
        return this.$refs.list.checkCanSave()
      }
      return true
    },
    projectSaved: function () {
      this.showTabs = true
    },
    saveProject: async function () {
      if (this.$refs.list) {
        await this.$refs.list.savePosts()
        this.$refs.projectHeader.saveEdit()
      } else if (this.$refs.grid) {
        this.$refs.grid.savePosts()
        this.$refs.projectHeader.saveEdit()
      } else {
        if (!this.$refs.list) {
          this._set(state.activeTab, 'list')
        }
        this.$nextTick(async () => {
          await this.$refs.list.savePosts()
          this.$refs.projectHeader.saveEdit()
        })
      }
    },
    isDeleting: function (value) {
      this.isDeletingInProgress = value
    },
    sort: function () {
      if (!this.isSortedNewestFirst) {
        this._callback(state.posts.sort)
      }
    },
    incrementPostCount: function () {
      this._set(state.posts.total, this._get(state.posts.total) + 1)
    },
    attachScrollListener: function () {
      if (window !== null) {
        window.addEventListener('scroll', this.checkHeight)
      }
    },
    detachScrollListener: function () {
      if (window !== null) {
        window.removeEventListener('scroll', this.checkHeight)
      }
    },
    checkHeight: function () {
      this.projectHeight = this.$refs.projectView.offsetHeight
    },
    saveAndClose: function () {
      this.toggleShowCreate()
      this.scrollToSectionTop()
      this.setLastToFirst()
      this.incrementPostCount()
    },
    toggleShowCreate: function () {
      this.showCreate = !this.showCreate
      this.sort()
    },
    scrollToSectionTop: function () {
      const headerBottom = this.$refs.projectHeader.$refs.headerContainer.getBoundingClientRect()
      window.scrollTo({
        top: headerBottom.height,
        behavior: 'smooth'
      })
    },
    setLastToFirst: function () {
      this._set(state.project.defaultFirstToLast, false)
      this.sort()
    },
    updateIsOnline: debounce(function (appearOnline) {
      const params = { id: this.project.id, isOnline: appearOnline }
      this.$store.dispatch('updateProjectIsOnline', params).then((value) => {
        this._set(state.project.isOnline, value.isOnline)
      })
    }, 500, { leading: true, trailing: false })
  }
}
</script>

<style lang="scss">
@import '~styles/utils.scss';
.buttons-container {
  position: absolute;
}
.project-page {
  background-color: var(--background-fifth);
  min-height: calc(100vh - 52px);
  padding-bottom: 3rem;
  @include fix-ios-scrolling;

  @include mobile {
    padding-bottom: 2rem;
  }
}
.all-content {
  max-width: 800px; // controls the width of all components
  margin: 0 auto;
  background-color: white;
}

.loader-container {
  padding: 1rem;
  .loader {
    display: block;
    font-size: 2rem;
    margin: auto;
  }
}
.component {
  margin-top: 5rem;
  position: relative;
  overflow: visible;
}
.create-is-open {
  margin-top: 0;
}

.list {
  box-shadow: none;
  background-color: transparent;
}

.new-section-wrapper {
  padding: .75rem 0 0 0.75rem;
  padding-top: 2rem;
  z-index: 1;
}
.new-section {
  height: 100%;
  align-items: center;
  justify-content: center;
  color: $grey-dark;
  letter-spacing: .05rem;
  a {
    width: 130px;
    font-size: 0.65rem;
    border-radius: 0;
  }
  .button[disabled] {
    pointer-events: none;
  }
}
  .component {
    margin-top: 100px;
  }
@include mobile {
  .new-section-wrapper {
    padding: .65rem 1rem;
    padding-top: 2.1rem;
    font-size: .65rem;
  }
  .new-section {
    a {
      width: 85px;
    }
  }
}

</style>
